import { writable, Writable } from 'svelte/store';

/**
 * Site-section override for the Janus4 builder Live Preview.
 *
 * janus4 can render the same page JSON under different site sections (applying
 * that section's theme / datasources / {{siteSection}} via its fallback
 * mechanism). For a shared/default-env page, the author can preview it "as it
 * would look under section X" by overriding the section the CMS passed down.
 *
 * The override persists in localStorage (like the deployment-env selector) so
 * it survives flipping between editor modes. The builder is served per-tenant
 * (web-builder-{tricode}-{league}), so localStorage is already tenant-scoped.
 * The stored value is validated against the sections the current deployment
 * actually reports before being applied, so a stale value falls back to the
 * default cleanly.
 */

// Empty string = "Default (no section)" — preview under the page's own env.
const STORAGE_KEY = 'yc-builder-janus4-preview-section';

function loadStoredSection(): string {
  try {
    return window?.localStorage?.getItem(STORAGE_KEY) || '';
  } catch (e) {
    return '';
  }
}

/** User-selected section override (persisted). Empty string = Default. */
export const janus4SectionOverrideStore: Writable<string> = writable(loadStoredSection());

janus4SectionOverrideStore.subscribe((value) => {
  try {
    window?.localStorage?.setItem(STORAGE_KEY, value || '');
  } catch (e) {
    // localStorage unavailable - selection just won't persist
  }
});

/** Sections reported by the janus4 deployment (via postMessage on preview load). */
export const janus4AvailableSectionsStore: Writable<string[]> = writable([]);

/** The section the page belongs to per the CMS (its environment). */
export const janus4CmsSectionStore: Writable<string> = writable('');

/**
 * Map of environment slug -> human-readable name, supplied by the CMS, used to
 * label the section-override dropdown. Falls back to the slug when unmapped.
 */
export const janus4SectionNamesStore: Writable<Record<string, string>> = writable({});
